import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är en företagsfilm?",
    a: "Företagsfilm är film som berättar vad ett företag gör, står för eller erbjuder — för sajt, sociala kanaler, säljsamtal, rekrytering eller event. Bra företagsfilm är inte en presentation; den är en story som mottagaren orkar titta klart på.",
  },
  {
    q: "Vad kostar en företagsfilm?",
    a: "En enklare företagsfilm landar typiskt på 35–90 kkr. En mer påkostad produktion med flera inspelningsdagar, location-resor eller skådespelare ligger på 120–400 kkr. Pulse-abonnemang ger lägre kostnad per film vid löpande output.",
  },
  {
    q: "Hur lång ska en företagsfilm vara?",
    a: "För sajt-hero: 60–90 sekunder. För LinkedIn: 45–90 sekunder med textöverlägg. För säljmaterial och B2B: 90–180 sekunder. Korta klipp för paid social: 9–22 sekunder. Vi rekommenderar att skära varje produktion i 3–5 längder.",
  },
  {
    q: "Vad krävs av oss för att en företagsfilm ska bli bra?",
    a: "Tre saker: (1) en tydlig bild av vem mottagaren är och vad ni vill att hen ska göra, (2) några riktiga människor som är beredda att vara med — autentiska röster slår skådespelare 9 av 10 gånger för B2B, (3) tid till ett strategimöte före inspelning. Allt annat löser vi.",
  },
  {
    q: "Filmar ni hos oss eller i studio?",
    a: "Båda. För företagsfilm rekommenderar vi oftast att filma i er verkliga miljö — det syns på filmen. Vi har studio i Göteborg om talking-head-format passar bättre.",
  },
  {
    q: "Får vi använda företagsfilmen var vi vill?",
    a: "Ja. Allt slutmaterial är ert att använda fritt — sajt, sociala kanaler, säljpresentationer, event, rekrytering. Vi behåller rätten att visa utvalda klipp i vår portfolio om inget annat avtalas.",
  },
];

export const Route = createFileRoute("/foretagsfilm")({
  head: () => ({
    meta: [
      { title: "Företagsfilm — som faktiskt blir sedd | Fenny" },
      {
        name: "description",
        content:
          "Företagsfilm från Fenny. Strategi, regi, foto och versionering för sajt, sociala kanaler, sälj och rekrytering. Första klippet inom 10–14 dagar med Pulse-abonnemang.",
      },
      { property: "og:title", content: "Företagsfilm — Fenny" },
      { property: "og:description", content: "Företagsfilm som mottagaren orkar titta klart på — för sajt, sociala kanaler, sälj och rekrytering." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/foretagsfilm" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/foretagsfilm" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/foretagsfilm", name: "Företagsfilm — som faktiskt blir sedd | Fenny", description: "Företagsfilm från Fenny. Strategi, regi, foto och versionering för sajt, sociala kanaler, sälj och rekrytering. Första klippet inom 10–14 dagar med Pulse-abonnemang."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Företagsfilm",
            path: "/foretagsfilm",
            serviceType: "Produktion av företagsfilm",
            description:
              "Företagsfilm för sajt, sociala kanaler, säljmaterial, employer branding och event. Strategi, regi, foto, klipp, grade och versionering.",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Företagsfilm", path: "/foretagsfilm" },
          ]),
        ),
      },
    ],
  }),
  component: ForetagsfilmPage,
});

function ForetagsfilmPage() {
  return (
    <LandingPage
      eyebrow="Företagsfilm"
      title={<>Företagsfilm som faktiskt blir sedd — inte arkiverad.</>}
      lede="Vi producerar företagsfilm för sajt, LinkedIn, sälj och rekrytering. Inte en presentation — en story som mottagaren orkar titta klart på och delar vidare. Strategi, regi, foto, klipp och versionering i en process."
      tldr={<p>Företagsfilm är rörligt innehåll som förklarar vad ett företag gör, varför det spelar roll och vem som står bakom — för sajt, sälj, LinkedIn och rekrytering. Fenny producerar företagsfilm från <strong>ca 35 000 kr</strong> per produktion eller löpande via <strong>Pulse-abonnemang från 14 995 kr/mån</strong>. Leverans i alla format (16:9, 9:16, 1:1).</p>}
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="foretagsfilm"
      relatedLinksFor="/foretagsfilm"
      quickAnswerId="sv_vad_ar_foretagsfilm"
      trust={[
        { value: "35–400 kkr", label: "typiskt prisspann" },
        { value: "3–5", label: "färdiga filmer per inspelning" },
        { value: "3–6 v", label: "brief till leverans" },
      ]}
      pricing={{
        heading: "Vad en företagsfilm kostar",
        intro: "Riktpriser 2026. Antalet inspelningsdagar och resor styr priset mer än filmens längd.",
        rows: [
          { format: "Grundläggande företagsfilm", scope: "1 dag, en location, egna medarbetare, 90 sek + 3 cutdowns", price: "35 000–90 000 kr", lead: "3–4 veckor" },
          { format: "Utökad produktion", scope: "2 dagar, flera orter eller anläggningar, drönare och b-roll", price: "120 000–250 000 kr", lead: "5–6 veckor" },
          { format: "Påkostad företagsfilm", scope: "3+ dagar, skådespelare, originalmusik, engelsk version", price: "250 000–400 000 kr", lead: "7–9 veckor" },
        ],
        footnote: "Exkl. moms. Löpande behov blir billigare per film via Pulse än som separata projekt.",
      }}
      objections={{
        heading: "Det ni brukar undra",
        items: [
          { objection: "Räcker det inte med en film?", answer: <>En film räcker för sajten. Men samma inspelning ger normalt tre till fem klipp för LinkedIn, sälj och rekrytering — så gott som gratis om vi planerar för det innan kameran går igång.</> },
          { objection: "Vi har inget manus.", answer: <>Det behöver ni inte ha. Vi gör en intervjubaserad researchrunda och skriver strukturen åt er; ni godkänner innan inspelning.</> },
        ],
      }}
      bullets={[
        { title: "Riktiga människor", body: "Vi använder era egna människor framför kameran när det går. Autentiska röster slår skådespelare 9 av 10 gånger för B2B." },
        { title: "Versioner ingår", body: "Sajt-hero, LinkedIn-cut, kortklipp för paid, undertexter — en inspelning blir 3–5 färdiga filmer." },
        { title: "Tydligt syfte", body: "Vi börjar med vad ni vill att mottagaren ska göra eller känna efter. Filmen byggs mot det." },
      ]}
      sections={[
        {
          id: "anvandningsomraden",
          heading: "När använder man en företagsfilm?",
          body: (
            <>
              <p>
                Sajt-hero (60–90 s) som öppnar med en mänsklig hook istället för en logo-resa. LinkedIn-presentationer (45–90 s) som driver inbound. Säljmaterial (90–180 s) som ersätter det femte mailet. Rekryteringsfilm som faktiskt visar hur det är att jobba hos er. Eventfilm för stämmor, jubileum eller lanseringar.
              </p>
              <p>
                Många kunder kör en stor produktion per år och kompletterar med löpande sociala-cuts via <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/pulse">Pulse-abonnemang</a>.
              </p>
            </>
          ),
        },
        {
          id: "process",
          heading: "Hur går processen till?",
          body: (
            <p>
              Strategimöte (1 h, kostnadsfritt). Manus och planering (5–7 dagar). Inspelning (1 dag i regel). Klipp, grade, ljud och versionering (2 veckor). Leverans i delad mapp. Total tid: 3–5 veckor från brief till färdig film.
            </p>
          ),
        },
        {
          id: "film-till-foretag",
          heading: "Film till företag — vad heter det egentligen?",
          body: (
            <p>
              &quot;Företagsfilm&quot;, &quot;film till företag&quot;,
              &quot;företagsvideo&quot; och &quot;produktionsbolag för
              företagsfilm&quot; beskriver samma behov: rörligt innehåll som
              förklarar, säljer eller rekryterar. Vill ni ha renodlad
              kampanjfilm är{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">reklamfilm</a>{" "}
              rätt spår, och ska filmen sändas i tv gäller{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/tv-produktion">tv-produktion</a>{" "}
              med egna leveranskrav. Behöver ni animerad företagsfilm gör vi den via{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/motion-graphics">motion graphics</a>.
            </p>
          ),
        },
        {
          id: "pris",
          heading: "Vad kostar en företagsfilm?",
          body: (
            <p>
              Spannet är brett. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/vad-kostar-en-foretagsfilm">Läs vår genomgång av vad en företagsfilm kostar</a> eller <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/priser">räkna fram er nivå direkt</a>. Pulse-abonnemang sänker kostnaden per film vid löpande output.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={6}
      riskReducers
      leadMagnet={{
        slug: "manadsvis-filmplan",
        title: "Ladda ner: mall för månadsvis filmplan",
        description: "Strukturen ”ankare + ekon” som gör att ni aldrig står utan material — samma mall vi kör med Pulse-kunder.",
      }}
      closing={{
        title: "Dags att uppdatera er företagsfilm?",
        body: "30 minuter där vi pekar ut tre konkreta filmer ni borde göra först.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Räkna pris", to: "/priser" },
      }}
    />
  );
}
